import type { InfrastructureHealth } from '../api.infrastructure';

export type DeepPathKind = 'cloud' | 'gpu' | 'waiting_gpu' | 'waiting_then_cloud' | 'unknown';

export type DeepPathView = {
  kind: DeepPathKind;
  /** Chip label, e.g. "Deep · Wake". */
  label: string;
  /** Short plain-text for aria / Details value. */
  short: string;
  /** Operator-facing explanation. */
  summary: string;
};

/** Canonical engine wire value; maps legacy taxonomy aliases. */
export function normalizeModerationEngine(raw: string | null | undefined): string {
  const src = (raw ?? '').toLowerCase();
  if (src === 'gemma' || src === 'gpu') return 'gpu';
  if (src === 'bedrock' || src === 'cloud') return 'cloud';
  if (src === 'dual' || src === 'gpu_with_cloud_fallback') return 'gpu_with_cloud_fallback';
  return src;
}

function gpuIsReady(data: InfrastructureHealth | undefined): boolean {
  return (data?.gpu?.status ?? 'unknown') === 'ready' || data?.gpu?.ready === true;
}

/**
 * Env-level effective deep path from stack health (not per-post route).
 * Under gpu_with_cloud_fallback + GPU offline, wake is armed in the background
 * and new deep tasks run on Cloud immediately (no wake-timeout poll).
 * `not_configured` and `unknown` are not a scale-to-zero wake.
 */
export function deepPathFromHealth(
  data: InfrastructureHealth | undefined,
  opts?: { loading?: boolean; mediaError?: boolean },
): DeepPathView {
  if (opts?.loading) {
    return {
      kind: 'unknown',
      label: 'Deep · …',
      short: '…',
      summary: 'Loading stack status…',
    };
  }

  if (opts?.mediaError || !data?.mediaManagerReachable || !data?.tipmiApiReachable) {
    return {
      kind: 'unknown',
      label: 'Deep · —',
      short: '—',
      summary: 'Needs reachable tipmi-api and media-manager to derive the deep path.',
    };
  }

  const engine = normalizeModerationEngine(data.moderationEngine ?? data.taxonomySource);
  if (!engine) {
    return {
      kind: 'unknown',
      label: 'Deep · —',
      short: '—',
      summary: 'TIPMI_MODERATION_ENGINE not reported by tipmi-api.',
    };
  }

  if (engine === 'cloud') {
    return {
      kind: 'cloud',
      label: 'Deep · Cloud',
      short: 'Cloud',
      summary: 'Engine is Cloud-only — deep skips GPU wake and uses Cloud model moderation.',
    };
  }

  const ready = gpuIsReady(data);
  const gpuStatus = data.gpu?.status ?? 'unknown';

  if (engine === 'gpu') {
    if (ready) {
      return {
        kind: 'gpu',
        label: 'Deep · GPU',
        short: 'GPU',
        summary: 'GPU is ready — deep runs on the vision / GPU model server.',
      };
    }
    if (gpuStatus === 'offline') {
      return {
        kind: 'waiting_gpu',
        label: 'Deep · Wait',
        short: 'Waiting GPU',
        summary:
          'Engine is GPU-only and GPU is not ready. Deep waits for GPU wake (scale-from-zero); posts can sit WAT while wake runs.',
      };
    }
    if (gpuStatus === 'not_configured') {
      return {
        kind: 'unknown',
        label: 'Deep · N/A',
        short: 'N/A',
        summary:
          'GPU is not configured (SAFEGUARD_INFERENCE_URL unset). GPU-only deep fails; there is no GPU to wake.',
      };
    }
    return {
      kind: 'unknown',
      label: 'Deep · Unknown',
      short: 'Unknown',
      summary: 'GPU status is unknown, so the effective deep path is not a scale-to-zero wake.',
    };
  }

  if (engine === 'gpu_with_cloud_fallback') {
    if (ready) {
      return {
        kind: 'gpu',
        label: 'Deep · GPU',
        short: 'GPU',
        summary: 'GPU is ready — deep prefers GPU; Cloud is only for fallback.',
      };
    }
    if (gpuStatus === 'offline') {
      return {
        kind: 'waiting_then_cloud',
        label: 'Deep · Wake',
        short: 'Cloud (wake)',
        summary:
          'GPU offline. Wake is armed in the background; new deep tasks run on Cloud now. Long “scanning” is usually Cloud/frame latency, not the GPU wake timeout.',
      };
    }
    if (gpuStatus === 'not_configured') {
      return {
        kind: 'cloud',
        label: 'Deep · Cloud',
        short: 'Cloud',
        summary:
          'GPU is not configured (SAFEGUARD_INFERENCE_URL unset), so there is no wake window. Deep uses Cloud without waiting for GPU.',
      };
    }
    return {
      kind: 'unknown',
      label: 'Deep · Unknown',
      short: 'Unknown',
      summary: 'GPU status is unknown, so the effective deep path is not Cloud-with-wake-armed.',
    };
  }

  return {
    kind: 'unknown',
    label: 'Deep · —',
    short: '—',
    summary: `Unrecognized moderation engine: ${engine}`,
  };
}
